import styles from "./InfoView.module.css";
import PropTypes from "prop-types";
import { Button, Checkbox, TextInput, Checkbubble } from "@wwg/ui";
import { useTranslation } from "react-i18next";
import { PREFERRED_DELIVERY } from "shared/enums";

function Label({ children, fallback = "N/A" }) {
  return children || <span className={styles.placeholder}>{fallback}</span>;
}

/**
 * Component for viewing customer info. Takes a `contact` data object.
 */
export function InfoView({ info, onEdit }) {
  const {t} = useTranslation();
  const emptyText = "N/A";

  return (
    <div className={styles.container}>
      <div className={styles.address}>
        <div className={styles.row}>
          <div>
            <div>
              <Label fallback={t("Street")}>{info.street}</Label>
            </div>
            <div>
              <Label fallback={t("City")}>{info.city}</Label>,{" "}
              <Label fallback={t("State")}>{info.state}</Label>
            </div>
            <div>
              <Label fallback={t("Zip")}>{info.zip}</Label>
            </div>
          </div>

          <div>
            <Button buttonStyle="hollow" onClick={onEdit}>
              {t("buttons.edit")}
            </Button>
          </div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <h5>{t("client.clientNumber")}</h5>
            <div>{info.clientNumber || emptyText}</div>
          </div>
        </div>

        <div className={styles.splitGroup}>
          <div className={styles.col}>
            <div className={styles.inputGroup}>
              <h5>{t("client.Occupation")}</h5>
              <div>{info.occupation || emptyText}</div>
            </div>
            <div className={styles.inputGroup}>
              <h5>{t("client.Spouse")}</h5>
              <div>{info.spouse || emptyText}</div>
            </div>
          </div>
          <div className={styles.col}>
            <div className={styles.inputGroup}>
              <h5>{t("client.Birthday")}</h5>
              <div>{info.birthday || emptyText}</div>
            </div>
            <div className={styles.inputGroup}>
              <h5>{t("client.Anniversary")}</h5>
              <div>{info.anniversary || emptyText}</div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.section}>
        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <h5>{t("client.dittoStartDate")}</h5>
            <div>{info.dittoStartDate || emptyText}</div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <h5>{t("client.preferredDelivery")}</h5>
            <div className={styles.fields}>
              <div className={styles.checkBubbleGroup}>
                <Checkbubble
                  id="delivery-ground"
                  isDisabled={true}
                  isChecked={
                    info.preferredDeliveryMethod === PREFERRED_DELIVERY.Ground
                  }
                />

                <label htmlFor="delivery-ground">{t("client.Ground")}</label>
              </div>

              <div className={styles.checkBubbleGroup}>
                <Checkbubble
                  id="delivery-expedited"
                  isDisabled={true}
                  isChecked={
                    info.preferredDeliveryMethod ===
                    PREFERRED_DELIVERY.Expedited
                  }
                />

                <label htmlFor="delivery-expedited">
                  {t("client.Expedited")}
                </label>
              </div>

              <div className={styles.checkBubbleGroup}>
                <Checkbubble
                  id="delivery-amway"
                  isDisabled={true}
                  isChecked={
                    info.preferredDeliveryMethod ===
                    PREFERRED_DELIVERY.AmwayPremium
                  }
                />

                <label htmlFor="delivery-amway">
                  {t("client.premiumDelivery")}&trade;
                </label>
              </div>

              <div className={styles.checkBubbleGroup}>
                <Checkbubble
                  id="delivery-hand"
                  isDisabled={true}
                  isChecked={
                    info.preferredDeliveryMethod ===
                    PREFERRED_DELIVERY.HandDelivery
                  }
                />

                <label htmlFor="delivery-hand">
                  {t("client.handDelivery")}
                </label>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <h5>{t("client.gateCode")}</h5>
            <div>{info.gateCode || emptyText}</div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <h5>{t("client.specialLocationDropOff")}</h5>
            <div>{info.dropOffLocation || emptyText}</div>
          </div>
        </div>
      </div>
      <div>
        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <h5>
              {t("client.dietaryPreferences")} <br />
              <span>{t("client.glutenFree+")}</span>
            </h5>
            <div>{info.dietaryPreferences || emptyText}</div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <h5>
              {t("client.Allergies")} <br />
              <span>{t("client.nuts+")}</span>
            </h5>
            <div>{info.allergies || emptyText}</div>
          </div>
        </div>
        <div className={styles.row}>
          <div className={styles.inputGroup}>
            <h5>{t("client.healthConcerns")}</h5>
            <div>{info.healthConcerns || emptyText}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

InfoView.propTypes = {
  info: PropTypes.shape({
    street: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
    clientNumber: PropTypes.string,
    occupation: PropTypes.string,
    birthday: PropTypes.string,
    spouse: PropTypes.string,
    anniversary: PropTypes.string,
    dittoStartDate: PropTypes.string,
    preferredDeliveryMethod: PropTypes.string,
    gateCode: PropTypes.string,
    dropOffLocation: PropTypes.string,
    dietaryPreferences: PropTypes.string,
    allergies: PropTypes.string,
    healthConcerns: PropTypes.string,
  }),

  onEdit: PropTypes.func,
};
